import * as THREE from "three";
import gsap from "gsap";
import { Page } from "./Page";

export interface CardTextures {
  outsideFront: THREE.Texture | null; // visible when card is closed
  insideLeft: THREE.Texture | null;   // back of the front cover, visible when open
  insideRight: THREE.Texture | null;  // the message page
  outsideBack: THREE.Texture | null;  // bottom of card; rarely seen
}

export interface CardOptions {
  width: number;
  height: number;
  textures?: Partial<CardTextures>;
}

/**
 * A two-page card: a flippable front cover hinged at x=0, sitting on top of a
 * static base page. Closed -> see the cover (outside-front). Open -> the cover
 * has rotated 180° to lie on the left, exposing inside-left (back of cover) and
 * inside-right (the base's front face).
 */
export class Card {
  readonly group = new THREE.Group();
  readonly base: Page;   // never flips; its front face = inside-right
  readonly cover: Page;  // flips around the spine at x=0

  private _isOpen = false;
  private _isAnimating = false;
  private static readonly LIFT = 0.005; // small z gap between cover and base

  /** Fired with the new open/closed state when an animation completes. */
  onToggle?: (isOpen: boolean) => void;
  /** Fired at the start of a flip with the target state. */
  onFlipStart?: (targetOpen: boolean) => void;
  /**
   * Fired every animation frame with normalized openness in [0..1].
   * 0 = fully closed, 1 = fully open. Direction-agnostic so external code
   * can drive layout (camera distance, root position) off the same value.
   */
  onFlipProgress?: (openness: number) => void;

  constructor(opts: CardOptions) {
    const t = opts.textures ?? {};

    this.base = new Page({
      width: opts.width,
      height: opts.height,
      frontTexture: t.insideRight ?? null,
      backTexture: t.outsideBack ?? null,
      segments: 16, // base never curls -> low subdivision is fine
    });
    this.base.flip = 0;
    this.base.mesh.position.z = 0;
    this.base.mesh.castShadow = true;
    this.base.mesh.receiveShadow = true;

    this.cover = new Page({
      width: opts.width,
      height: opts.height,
      frontTexture: t.outsideFront ?? null,
      backTexture: t.insideLeft ?? null,
      segments: 64,
    });
    this.cover.flip = 0;
    this.cover.mesh.position.z = Card.LIFT;
    this.cover.mesh.castShadow = true;
    this.cover.mesh.receiveShadow = true;

    this.group.add(this.base.mesh, this.cover.mesh);
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  get isAnimating(): boolean {
    return this._isAnimating;
  }

  setTextures(t: Partial<CardTextures>) {
    if (t.outsideFront !== undefined) this.cover.setFrontTexture(t.outsideFront);
    if (t.insideLeft !== undefined) this.cover.setBackTexture(t.insideLeft);
    if (t.insideRight !== undefined) this.base.setFrontTexture(t.insideRight);
    if (t.outsideBack !== undefined) this.base.setBackTexture(t.outsideBack);
  }

  open(): Promise<void> {
    if (this._isOpen) return Promise.resolve();
    return this.animate(0, Math.PI, true);
  }

  close(): Promise<void> {
    if (!this._isOpen) return Promise.resolve();
    return this.animate(Math.PI, 0, false);
  }

  toggle(): Promise<void> {
    return this._isOpen ? this.close() : this.open();
  }

  private animate(fromAngle: number, toAngle: number, targetOpen: boolean): Promise<void> {
    if (this._isAnimating) return Promise.resolve();
    this._isAnimating = true;
    this.onFlipStart?.(targetOpen);

    return new Promise<void>((resolve) => {
      const state = { t: 0 };
      this.cover.flip = fromAngle;
      // Lift the cover off the base for the whole flip so its back face never
      // z-fights with the base page.
      this.cover.mesh.position.z = Card.LIFT;

      gsap.to(state, {
        t: 1,
        duration: 1.2,
        ease: "power2.inOut",
        onUpdate: () => {
          const t = state.t;
          this.cover.flip = THREE.MathUtils.lerp(fromAngle, toAngle, t);
          // Float the cover up at midflip so it visibly arcs above the base.
          const arc = Math.sin(t * Math.PI) * 0.18;
          this.cover.mesh.position.z = Card.LIFT + arc;
          const openness = targetOpen ? t : 1 - t;
          this.onFlipProgress?.(openness);
        },
        onComplete: () => {
          this._isOpen = targetOpen;
          // When open the cover lies in -x; nudge it slightly below z=0 so it
          // rests just under the base on the left half (no visual effect since
          // the base only occupies x:[0..W]).
          this.cover.mesh.position.z = targetOpen ? -Card.LIFT : Card.LIFT;
          this._isAnimating = false;
          this.onFlipProgress?.(targetOpen ? 1 : 0);
          this.onToggle?.(targetOpen);
          resolve();
        },
      });
    });
  }
}
